import React from 'react';
import { toast } from 'react-toastify';
import { SuccessActionToast } from './SuccessActionToast';
import { useShellStore } from '../store/shellStore';
import {
  HIL_TOAST_AUTO_CLOSE_MS,
  HIL_TOAST_CONTAINER_ID,
} from '../../constants/timing';
import {
  isAgOpsHilRequestToast,
  resolveHilToastDedupeKeys,
  resolveHilToastId,
  type HilToastDedupeInput,
} from './resolveHilToastDedupeKeys';

export interface HilRaisedToastOptions extends HilToastDedupeInput {
  title?: string;
  description?: string;
}

const DEFAULT_TITLE = 'Run request submitted';
const DEFAULT_DESCRIPTION = 'Your request for runs is successfully done.';
const TOAST_DEDUPE_MS = 60_000;
const recentToastKeys = new Map<string, number>();

function openNotificationsInbox(): void {
  const { setEsaPanelMode, setEsaPanelOpen, setEsaInboxSubTab } = useShellStore.getState();
  setEsaInboxSubTab('notifications');
  setEsaPanelMode('inbox');
  setEsaPanelOpen(true);
}

function shouldSkipDuplicate(keys: string[]): boolean {
  if (keys.length === 0) {
    return false;
  }

  const now = Date.now();
  for (const key of keys) {
    const prev = recentToastKeys.get(key);
    if (prev != null && now - prev < TOAST_DEDUPE_MS) {
      return true;
    }
  }

  for (const key of keys) {
    recentToastKeys.set(key, now);
  }
  return false;
}

/** Test helper — clears dedupe state between unit tests. */
export function resetHilRaisedToastDedupeForTests(): void {
  recentToastKeys.clear();
}

/**
 * Shows the success toast when an AgOps hil_request is raised (SSE hil_request or canvas poll).
 * Relay/chat hil_escalation lines without a hil_request id are ignored.
 */
export function showHilRaisedToast(options: HilRaisedToastOptions = {}): void {
  if (!isAgOpsHilRequestToast(options)) {
    return;
  }

  const dedupeKeys = resolveHilToastDedupeKeys(options);
  if (shouldSkipDuplicate(dedupeKeys)) {
    return;
  }

  const toastId = resolveHilToastId(options, dedupeKeys);
  if (toast.isActive(toastId)) {
    return;
  }

  const title = (options.title || '').trim() || DEFAULT_TITLE;
  const description = (options.description || '').trim() || DEFAULT_DESCRIPTION;

  toast(
    ({ closeToast }) => (
      <SuccessActionToast
        title={title}
        description={description}
        closeToast={closeToast}
        onAction={() => {
          closeToast?.();
          openNotificationsInbox();
        }}
      />
    ),
    {
      toastId,
      containerId: HIL_TOAST_CONTAINER_ID,
      position: 'bottom-right',
      autoClose: HIL_TOAST_AUTO_CLOSE_MS,
      closeButton: false,
      hideProgressBar: false,
      pauseOnFocusLoss: false,
      icon: false,
      className: 'gaxl-success-action-toast',
      closeOnClick: false,
      draggable: false,
    }
  );
}
